import { Inject, Injectable } from '@angular/core';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { WINDOW } from '@lsq-frontend/angular-shared/settings';
import { AbstractAnalyticsService } from './abstract-analytics.service';
import { AnalyticsEvent, AnalyticsProp, AnalyticsValue } from './analytics.constants';

export interface GTWindow extends Window {
    dataLayer: any;
    gtag: any;
}

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService implements AbstractAnalyticsService {
    public googleAnalyticsId: string;

    constructor(
        @Inject(SETTINGS) private _settings: ISettings,
        @Inject(WINDOW) private _window: GTWindow
    ) {
        SingletonServiceGuard.register('GoogleAnalyticsService');
    }

    public initalize(internalUser: boolean): void {
        if (this.googleAnalyticsId) {
            return;
        }

        this.googleAnalyticsId = internalUser
            ? this._settings.internalGoogleAnalyticsId
            : this._settings.googleAnalyticsId;
        if (!this.googleAnalyticsId) {
            return;
        }

        this._setGtag();

        this._window.gtag('js', new Date());
        this._window.gtag('config', this.googleAnalyticsId, { send_page_view: false });
    }

    // Google Analytics does not allow us to identify users so do not implement these.
    public setCompanyData(
        companyName: string,
        email: string,
        userKey: string,
        firstName: string,
        lastName: string,
        isAdmin: boolean
    ): void {}
    public clearIdentifiersData(): void {}
    public setUserData(userName: string, email: string, userKey: string, isAdmin: boolean): void {}

    public setPageView(path: string, title: string): void {
        if (this._window.gtag) {
            this._window.gtag('config', this.googleAnalyticsId, {
                page_path: path,
                page_title: title
            });
        }
    }

    public triggerSingleDateFilterEvent(reportName: string, dateOffset: number): void {
        if (this._window.gtag) {
            this._window.gtag('event', AnalyticsEvent.Filter, {
                event_category: AnalyticsValue.EventCategoryFilter,
                [AnalyticsProp.ReportName]: reportName,
                [AnalyticsProp.FilterType]: AnalyticsValue.DateFilter,
                value: dateOffset
            });
        }
    }

    public triggerDateRangeFilterEvent(
        reportName: string,
        startDateOffset: number,
        endDateOffset: number
    ): void {
        if (this._window.gtag) {
            this._window.gtag('event', AnalyticsEvent.Filter, {
                event_category: AnalyticsValue.EventCategoryFilter,
                [AnalyticsProp.ReportName]: reportName,
                [AnalyticsProp.FilterType]: AnalyticsValue.DateFilter
            });
        }
    }

    public triggerSimpleEvent(eventName: string): void {
        if (this._window.gtag) {
            this._window.gtag('event', eventName);
        }
    }

    public triggerEventWithPayload(eventName: string, data: { [key: string]: any }): void {
        if (this._window.gtag) {
            // Google does not handle complex events, the only parameters are based around financial transactions.
            this._window.gtag('event', eventName);
        }
    }

    private _setGtag(): void {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.googleAnalyticsId;
        document.getElementsByTagName('head')[0].appendChild(script);
        this._window.dataLayer = this._window.dataLayer || [];
        const dataLayer = this._window.dataLayer;
        function gtag() {
            dataLayer.push(arguments);
        }
        this._window.gtag = gtag;
    }
}
